import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ChevronRight, Quote } from 'lucide-react';
import { dishHistories, images } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const HistoryPage = () => {
  const { t, lang } = useLanguage();
  const L = (obj, f) => (lang === 'en' && obj[`${f}_en`]) || obj[f];
  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="bg-[#F5E6D3] py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#0f172a] mb-4">
            {t('history', 'title')}
          </h1>
          <p className="text-lg text-[#0f172a]/80 max-w-2xl mx-auto">
            {t('history', 'subtitle')}
          </p>
        </div>
      </section>

      {/* Dishes History */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          {dishHistories.map((dish, index) => (
            <article
              key={dish.id}
              className={`mb-20 last:mb-0 ${
                index % 2 === 0 ? '' : 'lg:flex-row-reverse'
              }`}
            >
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 !== 0 ? 'lg:[&>*:first-child]:order-2' : ''
              }`}>
                {/* Image */}
                <div className="relative">
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={images[dish.image]}
                      alt={L(dish, 'name')}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-4 -right-4 bg-[#F5E6D3] px-6 py-3 rounded-lg shadow-lg">
                    <div className="flex items-center gap-2 text-[#8B4513]">
                      <MapPin size={18} />
                      <span className="font-medium">{L(dish, 'origin')}</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div>
                  <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0f172a] mb-6">
                    {L(dish, 'name')}
                  </h2>
                  <p className="text-lg text-[#64748b] mb-6 leading-relaxed">
                    {L(dish, 'description')}
                  </p>
                  <p className="text-[#64748b] mb-8 leading-relaxed">
                    {L(dish, 'history')}
                  </p>
                  
                  {/* Quote */}
                  <div className="bg-[#f8fafc] p-6 rounded-xl border-l-4 border-[#D4A853]">
                    <div className="flex gap-4">
                      <Quote size={24} className="text-[#D4A853] flex-shrink-0" />
                      <p className="text-[#0f172a] italic">
                        {L(dish, 'quote')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Decorative Section */}
      <section className="py-20 bg-[#f8fafc]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0f172a] mb-6">
            {t('history', 'millennialTitle')}
          </h2>
          <p className="text-lg text-[#64748b] mb-8 leading-relaxed">
            {t('history', 'millennialText')}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-12">
            {[
              { number: '5000+', label: t('history', 'statYears') },
              { number: '300+', label: t('history', 'statRecipes') },
              { number: '40+', label: t('history', 'statMezzes') },
              { number: '1', label: t('history', 'statPassion') },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <p className="font-serif text-3xl md:text-4xl font-bold text-[#D4A853]">
                  {stat.number}
                </p>
                <p className="text-[#64748b] mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#F5E6D3]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl font-bold text-[#0f172a] mb-4">
            {t('history', 'ctaTitle')}
          </h2>
          <p className="text-lg text-[#0f172a]/80 mb-8">
            {t('history', 'ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/reservation"
              className="inline-flex items-center justify-center gap-2 bg-[#0f172a] text-white px-8 py-4 rounded-md font-semibold hover:bg-[#1e293b] transition-all duration-200 hover:-translate-y-1 shadow-lg"
            >
              {t('history', 'bookTable')}
              <ChevronRight size={20} />
            </Link>
            <Link
              to="/menu"
              className="inline-flex items-center justify-center gap-2 bg-transparent text-[#0f172a] border-2 border-[#0f172a] px-8 py-4 rounded-md font-semibold hover:bg-[#0f172a] hover:text-white transition-all duration-200"
            >
              {t('history', 'viewMenu')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HistoryPage;
